import { useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { Button } from '@carbon/react';
import { TrashCan } from '@carbon/react/icons';

import { Section } from 'components/section';
import { Link } from 'components/link';
import { EmptyState } from 'components/empty-state';
import { Date } from 'components/date';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { DestroyConfirmation } from 'components/destroy-confirmation';
import { isTiffFileName } from 'components/allocation-manager/allocation-tracts-table/utils';
import DocumentSvg from 'images/srp-illustration-document.svg';

import { deleteAfeSupportingFileLoadableAtom } from '../atoms';
import type { AfeDetailSupportingFile } from '../types';
import { formatBytes } from '../utils';
import styles from './supporting-files-panel.module.scss';

interface SupportingFilesPanelProps {
  files: AfeDetailSupportingFile[];
  afeId: number;
}

// `DataTable<T>` constrains `T extends Record<string, unknown>`. Intersect
// with the index signature so column cells still see the original entity
// shape via `row.original`.
type SupportingFileRow = AfeDetailSupportingFile & Record<string, unknown>;

/** All-features-off DataTable config. Note: `fitContent` is intentionally
 * omitted (defaults to false) so columns distribute the container's width
 * rather than collapsing to content-fit. The summary-step.tsx variant uses
 * `fitContent: true` because it lives in a narrow sidebar; this panel is
 * full-width and needs the stretch behavior. */
const READONLY_TABLE_CONFIG = {
  sorting: false,
  columnResizing: false,
  columnReordering: false,
  columnPinning: false,
  columnVisibility: false,
  globalSearch: false,
  columnFilters: false,
  hideFooter: true,
} as const;

/**
 * Renders the AFE detail's Supporting Files area: a `DataTable` of attached
 * files with per-row Delete (gated by a confirmation modal). Successful
 * deletes hide the row via a local `Set<number>` of deleted ids — the
 * upstream `useAfeDetailAtom` is intentionally not refetched (see Story 2
 * frontend-design Caveat #3).
 * @param {SupportingFilesPanelProps} props - Files from AFE detail + AFE id.
 * @return {JSX.Element} The supporting files section.
 */
const SupportingFilesPanel = ({ files, afeId }: SupportingFilesPanelProps) => {
  const [fileToDelete, setFileToDelete] =
    useState<AfeDetailSupportingFile | null>(null);
  const [localDeletedIds, setLocalDeletedIds] = useState<Set<number>>(
    () => new Set()
  );
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

  const [deleteLoadable, triggerDelete] = useAtom(
    deleteAfeSupportingFileLoadableAtom
  );
  const isDeleting = deleteLoadable.state === 'loading';

  // Short-circuit the filter when nothing has been deleted so the original
  // `files` reference is forwarded to DataTable unchanged — avoids per-render
  // identity churn that would defeat TanStack Table's row-identity caching.
  const visibleFiles = useMemo(() => {
    if (localDeletedIds.size === 0) return files;
    return files.filter((f) => !localDeletedIds.has(f.id));
  }, [files, localDeletedIds]);

  const handleCancelDelete = () => {
    // Block close while in flight — committed delete should not be backgrounded.
    if (isDeleting) return;
    setDeleteError(undefined);
    setFileToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;
    setDeleteError(undefined);
    try {
      await triggerDelete(afeId, fileToDelete.id);
      setLocalDeletedIds((prev) => new Set([...prev, fileToDelete.id]));
      setFileToDelete(null);
    } catch {
      // Mirrors Story 1's deleteAfeAtom flow in detail/page.tsx (keep modal
      // open + inline error) rather than the frontend-design stub
      // ("modal closes") — keeps failure-path UX consistent across the page.
      // Per-status copy is TBD (Open Q #3); generic message for now.
      setDeleteError('Unable to delete document.');
    }
  };

  const columns = useMemo<DataTableColumnDef<SupportingFileRow>[]>(
    () => [
      {
        id: 'fileName',
        accessorKey: 'fileName',
        header: 'File Name',
        size: 360,
        meta: { type: 'text' },
        cell: ({ row }) => {
          const file = row.original;
          if (!file.attachmentHref) return file.fileName;
          return (
            <Link
              truncate
              to={file.attachmentHref}
              target="_blank"
              rel="noreferrer"
              onClick={(e: React.MouseEvent) => {
                // TIFF/TIF inline rendering breaks in some browsers — force
                // window.open so the user gets a download/handler prompt
                // instead of a broken tab. Behavior preserved from the
                // pre-Story-2 panel.
                if (isTiffFileName(file.fileName)) {
                  e.preventDefault();
                  window.open(file.attachmentHref!, '_blank');
                }
              }}
            >
              {file.fileName}
            </Link>
          );
        },
      },
      {
        id: 'size',
        header: 'Size',
        size: 120,
        meta: { type: 'text' },
        cell: ({ row }) => {
          const { byteSize } = row.original;
          return byteSize !== null && byteSize !== undefined
            ? formatBytes(byteSize)
            : '--';
        },
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Uploaded',
        size: 140,
        meta: { type: 'text' },
        cell: ({ row }) => {
          const file = row.original;
          return file.createdAt ? (
            <Date date={file.createdAt} format="M/DD/YYYY" />
          ) : (
            '--'
          );
        },
      },
      {
        id: 'actions',
        header: '',
        size: 100,
        cell: ({ row }) => (
          <Button
            kind="danger--ghost"
            size="sm"
            renderIcon={TrashCan}
            onClick={() => {
              setDeleteError(undefined);
              setFileToDelete(row.original);
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <Section header="Supporting Files">
      {visibleFiles.length === 0 ? (
        <EmptyState
          icon={DocumentSvg}
          headerText="No Files Available"
          helperText="This AFE doesn't have any supporting files."
        />
      ) : (
        <div className={styles.tableContainer}>
          <DataTable<SupportingFileRow>
            data={visibleFiles as SupportingFileRow[]}
            columns={columns}
            getRowId={(row) => String(row.id)}
            config={READONLY_TABLE_CONFIG}
          />
        </div>
      )}

      <DestroyConfirmation
        open={fileToDelete !== null}
        onRequestClose={handleCancelDelete}
        onRequestSubmit={handleConfirmDelete}
        modalHeading="Delete Document"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        loadingStatus={isDeleting ? 'active' : 'inactive'}
        preventCloseOnClickOutside={isDeleting}
        error={deleteError}
      >
        Are you sure you want to delete{' '}
        <strong>{fileToDelete?.fileName}</strong>? This action cannot be undone.
      </DestroyConfirmation>
    </Section>
  );
};

export { SupportingFilesPanel };
